<div class="quick-link-form-page">
  <p-toast />
  <p-confirmDialog />

  <!-- LOADING SKELETON (Edit Mode) -->
  @if (loading() && mode() === "edit") {
    <div class="loading-skeleton-container">
      <!-- Desktop: Tabs Skeleton + Side Panel -->
      <div class="desktop-skeleton" *ngIf="!isMobile()">
        <div class="skeleton-layout">
          <!-- Form Column Skeleton -->
          <div class="skeleton-form-column">
            <!-- Tabs Navigation Skeleton -->
            <div class="tabs-skeleton">
              <div class="tab-skeleton-group">
                <p-skeleton width="120px" height="40px" styleClass="mr-3" />
                <p-skeleton width="140px" height="40px" styleClass="mr-3" />
                <p-skeleton width="150px" height="40px" />
              </div>
            </div>

            <!-- Tab Content Skeleton -->
            <div class="tab-content-skeleton">
              <div class="form-section-card">
                <!-- Section Header Skeleton -->
                <div class="section-header-skeleton">
                  <p-skeleton shape="circle" size="42px" styleClass="mr-3" />
                  <div class="flex-1">
                    <p-skeleton width="180px" height="24px" styleClass="mb-2" />
                    <p-skeleton width="280px" height="16px" />
                  </div>
                </div>

                <!-- Form Fields Skeleton (2-column grid) -->
                <div class="form-fields-skeleton">
                  <div class="field-row-skeleton">
                    <div class="field-skeleton">
                      <p-skeleton
                        width="140px"
                        height="18px"
                        styleClass="mb-2"
                      />
                      <p-skeleton width="100%" height="44px" />
                      <p-skeleton
                        width="200px"
                        height="14px"
                        styleClass="mt-1"
                      />
                    </div>
                    <div class="field-skeleton">
                      <p-skeleton
                        width="100px"
                        height="18px"
                        styleClass="mb-2"
                      />
                      <p-skeleton width="100%" height="44px" />
                      <p-skeleton
                        width="250px"
                        height="14px"
                        styleClass="mt-1"
                      />
                    </div>
                  </div>

                  <div class="field-skeleton">
                    <p-skeleton width="110px" height="18px" styleClass="mb-2" />
                    <p-skeleton width="100%" height="44px" />
                    <p-skeleton width="180px" height="14px" styleClass="mt-1" />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Side Panel Skeleton -->
          <div class="skeleton-side-panel">
            <div class="side-panel-skeleton-card">
              <!-- Header Skeleton -->
              <div class="side-panel-header-skeleton">
                <p-skeleton shape="circle" size="36px" styleClass="mr-3" />
                <div class="flex-1">
                  <p-skeleton width="100px" height="20px" styleClass="mb-2" />
                  <p-skeleton width="140px" height="14px" />
                </div>
              </div>

              <!-- Content Skeleton -->
              <div class="side-panel-content-skeleton">
                <!-- Section 1 -->
                <div class="preview-section-skeleton">
                  <p-skeleton width="90px" height="14px" styleClass="mb-3" />
                  <p-skeleton width="100%" height="60px" borderRadius="6px" />
                </div>

                <!-- Section 2 -->
                <div class="preview-section-skeleton">
                  <p-skeleton width="70px" height="14px" styleClass="mb-2" />
                  <p-skeleton
                    width="100%"
                    height="44px"
                    styleClass="mb-2"
                    borderRadius="6px"
                  />
                  <p-skeleton width="100%" height="60px" borderRadius="6px" />
                </div>

                <!-- Help Box -->
                <div class="help-box-skeleton">
                  <p-skeleton width="100%" height="40px" borderRadius="4px" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Mobile: Accordion Skeleton -->
      <div class="mobile-skeleton" *ngIf="isMobile()">
        <div class="accordion-skeleton-group">
          <p-skeleton
            width="100%"
            height="60px"
            styleClass="mb-3"
            borderRadius="10px"
          />
          <p-skeleton
            width="100%"
            height="60px"
            styleClass="mb-3"
            borderRadius="10px"
          />
          <p-skeleton width="100%" height="60px" borderRadius="10px" />
        </div>
      </div>
    </div>
  }

  <!-- Main Layout: Form + Side Panel (Desktop) / Accordion (Mobile) -->
  <div class="page-layout" *ngIf="!loading()">
    <!-- Form Column -->
    <div class="form-column">
      <!-- Form Container with Fixed Height -->
      <div class="form-container">
        <form
          [formGroup]="quickLinkForm"
          (ngSubmit)="onSubmit()"
          class="form-content"
        >
          <!-- MOBILE: Accordion Layout -->
          <div class="mobile-layout" *ngIf="isMobile()">
            <p-accordion [multiple]="false" [(value)]="activeAccordionIndex">
              <!-- Section 1: Basic Information -->
              <p-accordion-panel value="0">
                <p-accordion-header>
                  <div class="accordion-header">
                    <i class="pi pi-info-circle accordion-icon"></i>
                    <span class="accordion-title">Basic Information</span>
                    <p-tag
                      *ngIf="!isBasicInfoValid()"
                      value="Required"
                      severity="danger"
                      styleClass="ml-auto"
                    />
                  </div>
                </p-accordion-header>

                <p-accordion-content>
                  <div class="accordion-content">
                    <!-- Basic Info Content -->
                    <ng-container *ngTemplateOutlet="basicInfoContent" />
                  </div>
                </p-accordion-content>
              </p-accordion-panel>

              <!-- Section 2: Configuration -->
              <p-accordion-panel value="1">
                <p-accordion-header>
                  <div class="accordion-header">
                    <i class="pi pi-cog accordion-icon"></i>
                    <span class="accordion-title">Configuration</span>
                    <p-tag
                      *ngIf="isBasicInfoValid() && !isConfigValid()"
                      value="Incomplete"
                      severity="warning"
                      styleClass="ml-auto"
                    />
                  </div>
                </p-accordion-header>

                <p-accordion-content>
                  <div class="accordion-content">
                    <!-- Configuration Content -->
                    <ng-container *ngTemplateOutlet="configurationContent" />
                  </div>
                </p-accordion-content>
              </p-accordion-panel>

              <!-- Section 3: Redirect Settings -->
              <p-accordion-panel value="2">
                <p-accordion-header>
                  <div class="accordion-header">
                    <i class="pi pi-share-alt accordion-icon"></i>
                    <span class="accordion-title">Redirect Settings</span>
                    <p-tag
                      *ngIf="isConfigValid() && !isRedirectValid()"
                      value="Incomplete"
                      severity="warning"
                      styleClass="ml-auto"
                    />
                  </div>
                </p-accordion-header>

                <p-accordion-content>
                  <div class="accordion-content">
                    <!-- Redirect Content -->
                    <ng-container *ngTemplateOutlet="redirectContent" />
                  </div>
                </p-accordion-content>
              </p-accordion-panel>

              <!-- Section 4: Link Preview (Mobile Only - Edit Mode Only) -->
              <p-accordion-panel value="3" *ngIf="mode() === 'edit'">
                <p-accordion-header>
                  <div class="accordion-header">
                    <i class="pi pi-link accordion-icon"></i>
                    <span class="accordion-title">Link Preview</span>
                    @if (quickLinkId()) {
                      <p-tag
                        [value]="'ID: ' + quickLinkId()"
                        severity="secondary"
                        styleClass="text-xs ml-2"
                      />
                    }
                    @if (currentStatus() !== null) {
                      <p-tag
                        [value]="getStatusLabel(currentStatus())"
                        [severity]="getStatusSeverity(currentStatus())"
                        styleClass="text-xs ml-2"
                      />
                    }
                  </div>
                </p-accordion-header>

                <p-accordion-content>
                  <div class="accordion-content">
                    <!-- Link Preview Content for Mobile -->
                    <div class="mobile-preview-content">
                      <p class="mobile-preview-subtitle">
                        Copy this URL to embed in campaigns
                      </p>

                      <!-- Template URL -->
                      <div *ngIf="templateUrl()" class="mobile-preview-section">
                        <label class="mobile-preview-label">
                          <i class="pi pi-link"></i> Template URL
                        </label>
                        <div class="mobile-url-box">
                          <code class="mobile-url-text">{{
                            templateUrl()
                          }}</code>
                        </div>
                        <p-button
                          label="Copy Template URL"
                          icon="pi pi-copy"
                          (onClick)="copyUrlToClipboard(templateUrl()!)"
                          [outlined]="true"
                          size="small"
                          styleClass="w-full mt-2"
                        />
                      </div>

                      <!-- Example URL -->
                      <div
                        *ngIf="exampleUrl()"
                        class="mobile-preview-section mt-3"
                      >
                        <label class="mobile-preview-label">
                          <i class="pi pi-file"></i> Example
                        </label>

                        <!-- Live Preview Input -->
                        <div class="preview-input-group mb-2">
                          <label
                            for="previewSoIdMobile"
                            class="preview-input-label"
                          >
                            Scheduled Order ID
                          </label>
                          <input
                            pInputText
                            id="previewSoIdMobile"
                            type="text"
                            class="w-full preview-input"
                            placeholder="12345"
                            [value]="previewScheduledOrderId()"
                            (input)="
                              previewScheduledOrderId.set(
                                $any($event.target).value
                              )
                            "
                          />
                        </div>

                        <div class="mobile-url-box example-url">
                          <code class="mobile-url-text">{{
                            exampleUrl()
                          }}</code>
                        </div>
                        <p-button
                          label="Copy Example URL"
                          icon="pi pi-copy"
                          (onClick)="copyUrlToClipboard(exampleUrl()!)"
                          [outlined]="true"
                          size="small"
                          styleClass="w-full mt-2"
                        />
                      </div>

                      <!-- Help Message -->
                      <div *ngIf="templateUrl()" class="mobile-help-box mt-3">
                        <i class="pi pi-info-circle"></i>
                        <span
                          >Replace
                          <code>{{ "{" }}scheduled_order_id{{ "}" }}</code> with
                          actual ID</span
                        >
                      </div>

                      <!-- Single-Use Message -->
                      <div *ngIf="!templateUrl()" class="mobile-single-use-box">
                        <i class="pi pi-info-circle"></i>
                        <div>
                          <strong>Single-Use Links</strong>
                          <p>Generate unique tokens after creating the link</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </p-accordion-content>
              </p-accordion-panel>
            </p-accordion>
          </div>

          <!-- DESKTOP: Enhanced Tabs Layout -->
          <div class="desktop-layout" *ngIf="!isMobile()">
            <p-tabs
              scrollable
              [value]="activeTabIndex"
              (valueChange)="activeTabIndex = +$event"
            >
              <p-tablist>
                <!-- Basic Info Tab -->
                <p-tab [value]="0">
                  <div class="tab-header-content">
                    <i class="pi pi-info-circle mr-2"></i>
                    <span>Basic Info</span>
                    @if (!isBasicInfoValid()) {
                      <i
                        class="pi pi-exclamation-circle ml-2 tab-error-icon"
                        pTooltip="Required fields missing"
                        tooltipPosition="top"
                      ></i>
                    }
                  </div>
                </p-tab>

                <!-- Configuration Tab -->
                <p-tab [value]="1">
                  <div class="tab-header-content">
                    <i class="pi pi-cog mr-2"></i>
                    <span>Configuration</span>
                    @if (isBasicInfoValid() && !isConfigValid()) {
                      <i
                        class="pi pi-exclamation-circle ml-2 tab-error-icon"
                        pTooltip="Configuration incomplete"
                        tooltipPosition="top"
                      ></i>
                    }
                  </div>
                </p-tab>

                <!-- Redirect Settings Tab -->
                <p-tab [value]="2">
                  <div class="tab-header-content">
                    <i class="pi pi-share-alt mr-2"></i>
                    <span>Redirect Settings</span>
                    @if (isConfigValid() && !isRedirectValid()) {
                      <i
                        class="pi pi-exclamation-circle ml-2 tab-error-icon"
                        pTooltip="Redirect settings incomplete"
                        tooltipPosition="top"
                      ></i>
                    }
                  </div>
                </p-tab>
              </p-tablist>

              <p-tabpanels>
                <!-- Tab 1: Basic Info -->
                <p-tabpanel [value]="0">
                  <div class="tab-panel-content">
                    <div class="form-section-card">
                      <div class="section-header">
                        <div class="section-icon">
                          <i class="pi pi-info-circle"></i>
                        </div>
                        <div class="section-title-group">
                          <h3 class="section-title">Basic Information</h3>
                          <p class="section-subtitle">
                            Define the link name, slug, and action type
                          </p>
                        </div>
                      </div>

                      <!-- Basic Info Content -->
                      <ng-container *ngTemplateOutlet="basicInfoContent" />
                    </div>
                  </div>
                </p-tabpanel>

                <!-- Tab 2: Configuration -->
                <p-tabpanel [value]="1">
                  <div class="tab-panel-content">
                    <div class="form-section-card">
                      <div class="section-header">
                        <div class="section-icon">
                          <i class="pi pi-cog"></i>
                        </div>
                        <div class="section-title-group">
                          <h3 class="section-title">Configuration</h3>
                          <p class="section-subtitle">
                            Set usage limits, login requirements, and expiration
                          </p>
                        </div>
                      </div>

                      <!-- Configuration Content -->
                      <ng-container *ngTemplateOutlet="configurationContent" />
                    </div>
                  </div>
                </p-tabpanel>

                <!-- Tab 3: Redirect Settings -->
                <p-tabpanel [value]="2">
                  <div class="tab-panel-content">
                    <div class="form-section-card">
                      <div class="section-header">
                        <div class="section-icon">
                          <i class="pi pi-share-alt"></i>
                        </div>
                        <div class="section-title-group">
                          <h3 class="section-title">Redirect Settings</h3>
                          <p class="section-subtitle">
                            Configure where customers go after using the link
                          </p>
                        </div>
                      </div>

                      <!-- Redirect Content -->
                      <ng-container *ngTemplateOutlet="redirectContent" />
                    </div>
                  </div>
                </p-tabpanel>
              </p-tabpanels>
            </p-tabs>
          </div>
        </form>
      </div>
      <!-- End form-container -->
    </div>
    <!-- End form-column -->

    <!-- Side Panel Column (Desktop Only - Edit Mode Only) -->
    <div class="side-panel-column" *ngIf="!isMobile() && mode() === 'edit'">
      <div class="side-panel-sticky">
        <div class="side-panel-card">
          <!-- Header -->
          <div class="side-panel-header">
            <div class="side-panel-icon">
              <i class="pi pi-link"></i>
            </div>
            <div class="flex-1">
              <div class="flex items-center gap-2">
                <h3 class="side-panel-title">Link Preview</h3>
                @if (mode() === "edit" && quickLinkId()) {
                  <p-tag
                    [value]="'ID: ' + quickLinkId()"
                    severity="secondary"
                    styleClass="text-xs"
                  />
                }
                @if (mode() === "edit" && currentStatus() !== null) {
                  <p-tag
                    [value]="getStatusLabel(currentStatus())"
                    [severity]="getStatusSeverity(currentStatus())"
                    styleClass="text-xs"
                  />
                }
              </div>
              <p class="side-panel-subtitle">Your link will be</p>
            </div>
          </div>

          <!-- Content -->
          <div class="side-panel-content">
            <!-- Template URL Section -->
            <div *ngIf="templateUrl()" class="preview-section">
              <div class="preview-section-header">
                <i class="pi pi-link"></i>
                <span class="preview-section-title">Template URL</span>
              </div>
              <div class="url-box">
                <code class="url-text">{{ templateUrl() }}</code>
                <p-button
                  icon="pi pi-copy"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="copyUrlToClipboard(templateUrl()!)"
                  pTooltip="Copy template URL"
                  tooltipPosition="left"
                  size="small"
                  styleClass="copy-btn"
                />
              </div>
            </div>

            <!-- Example URL Section -->
            <div *ngIf="exampleUrl()" class="preview-section">
              <div class="preview-section-header">
                <i class="pi pi-file"></i>
                <span class="preview-section-title">Example</span>
              </div>

              <!-- Live Preview Input -->
              <div class="preview-input-group">
                <label for="previewSoId" class="preview-input-label">
                  Scheduled Order ID
                </label>
                <input
                  pInputText
                  id="previewSoId"
                  type="text"
                  class="w-full preview-input"
                  placeholder="12345"
                  [value]="previewScheduledOrderId()"
                  (input)="
                    previewScheduledOrderId.set($any($event.target).value)
                  "
                />
              </div>

              <div class="url-box example-url">
                <code class="url-text">{{ exampleUrl() }}</code>
                <p-button
                  icon="pi pi-copy"
                  [text]="true"
                  [rounded]="true"
                  (onClick)="copyUrlToClipboard(exampleUrl()!)"
                  pTooltip="Copy example URL"
                  tooltipPosition="left"
                  size="small"
                  styleClass="copy-btn"
                />
              </div>
            </div>

            <!-- Help Text -->
            <div *ngIf="templateUrl()" class="help-box">
              <i class="pi pi-info-circle help-icon"></i>
              <span class="help-text"
                >Replace <code>{{ "{" }}scheduled_order_id{{ "}" }}</code> with
                actual ID</span
              >
            </div>

            <!-- Single-Use Message -->
            <div *ngIf="!templateUrl()" class="single-use-box">
              <i class="pi pi-info-circle single-use-icon"></i>
              <div>
                <strong class="single-use-title">Single-Use Links</strong>
                <p class="single-use-text">
                  Generate unique tokens after creating the link
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- End side-panel-column -->
  </div>
  <!-- End page-layout -->

  <!-- Skeleton Action Buttons (only during loading) -->
  @if (loading() && mode() === "edit") {
    <div class="actions-container">
      <div class="actions-card">
        <div class="actions-buttons-skeleton">
          <p-skeleton width="90px" height="38px" styleClass="mr-3" />
          <p-skeleton width="130px" height="38px" />
        </div>
      </div>
    </div>
  }

  <!-- Sticky Action Buttons -->
  <div class="actions-container" *ngIf="!loading()">
    <div class="actions-card">
      <!-- Action Buttons -->
      <div class="actions-buttons">
        <p-button
          label="Cancel"
          severity="secondary"
          [outlined]="true"
          (onClick)="onCancel()"
          [disabled]="saving()"
          size="small"
        />
        @if (isDeleted()) {
          <!-- Show Permanent Delete button for deleted items -->
          <p-button
            label="Permanent Delete"
            severity="danger"
            (onClick)="onPermanentDelete()"
            [loading]="saving()"
            icon="pi pi-trash"
            size="small"
          />
        } @else {
          <!-- Show normal Save/Create button -->
          <p-button
            [label]="mode() === 'create' ? 'Create Link' : 'Save Changes'"
            (onClick)="onSubmit()"
            [disabled]="
              quickLinkForm.invalid || saving() || !isSlugVerifiedAndAvailable()
            "
            [loading]="saving()"
            icon="pi pi-check"
            size="small"
          />
        }
      </div>
    </div>
  </div>
</div>

<!-- ============================================ -->
<!-- TEMPLATE: Basic Information Content         -->
<!-- ============================================ -->
<ng-template #basicInfoContent>
  <div class="field-group">
    <!-- Name and Slug Row (2 columns on desktop) -->
    <div class="field-row-2">
      <!-- Name Field -->
      <div class="field">
        <label for="name" class="field-label">
          Quick Action Name
          <span class="required-indicator">*</span>
        </label>
        <input
          pInputText
          id="name"
          type="text"
          class="w-full"
          placeholder="e.g., Resume Your Subscription"
          [formControl]="quickLinkForm.controls.name"
          [class.ng-invalid]="
            quickLinkForm.controls.name.touched &&
            quickLinkForm.controls.name.invalid
          "
        />
        <small class="field-help"
          >This name is for your reference in the QMC</small
        >
        @if (
          quickLinkForm.controls.name.touched &&
          quickLinkForm.controls.name.invalid
        ) {
          <small class="field-error"
            >Name is required (max 255 characters)</small
          >
        }
      </div>

      <!-- Slug Field -->
      <div class="field">
        <label for="slug" class="field-label">
          Slug
          <span class="required-indicator">*</span>
        </label>
        <div class="flex gap-2 relative">
          <!-- Slug Input -->
          <div class="flex-1 relative">
            <input
              pInputText
              id="slug"
              type="text"
              class="w-full"
              placeholder="resume-your-subscription"
              [formControl]="quickLinkForm.controls.slug"
              (input)="onSlugChange()"
              [class.ng-invalid]="
                quickLinkForm.controls.slug.touched &&
                quickLinkForm.controls.slug.invalid
              "
              [disabled]="verifying()"
            />
            <!-- Skeleton overlay while verifying -->
            @if (verifying() && mode() === "create") {
              <div class="slug-input-skeleton-overlay">
                <p-skeleton width="100%" height="100%" />
              </div>
            }
          </div>

          <!-- Verify Button (only show when slug is taken) -->
          @if (
            mode() === "create" &&
            slugVerificationResult() &&
            !slugVerificationResult()!.isAvailable
          ) {
            <p-button
              label="Verify"
              icon="pi pi-check-circle"
              severity="secondary"
              [outlined]="true"
              size="small"
              (onClick)="verifySlug()"
              [loading]="verifying()"
              [disabled]="
                !quickLinkForm.controls.slug.value ||
                quickLinkForm.controls.slug.value.trim() === ''
              "
            />
          }
        </div>

        <!-- Verification Loading Skeleton -->
        @if (verifying() && mode() === "create") {
          <div class="slug-verification-skeleton mt-2">
            <p-skeleton height="2rem" styleClass="w-full" />
          </div>
        }

        <!-- Verification Result -->
        @if (slugVerificationResult() && !verifying()) {
          <div class="slug-verification-result mt-2">
            @if (slugVerificationResult()!.isAvailable) {
              <p-tag
                severity="success"
                icon="pi pi-check"
                value="Slug is available!"
                styleClass="w-full"
              />
            } @else {
              <p-tag
                severity="danger"
                icon="pi pi-times"
                [value]="
                  slugVerificationResult()!.message || 'Slug is not available'
                "
                styleClass="w-full"
              />
            }
          </div>
        }

        <small class="field-help">
          Auto-generated from name. Must be unique and URL-safe.
          @if (mode() === "edit") {
            <span class="text-orange-500"
              >Cannot be changed after creation.</span
            >
          }
        </small>
        @if (
          quickLinkForm.controls.slug.touched &&
          quickLinkForm.controls.slug.invalid
        ) {
          <small class="field-error"
            >Slug must be lowercase letters, numbers, and hyphens only</small
          >
        }
      </div>
    </div>

    <!-- Action Type Dropdown -->
    <div class="field">
      <label for="actionType" class="field-label">
        Action Type
        <span class="required-indicator">*</span>
      </label>
      <p-select
        [options]="actionTypeOptions"
        [formControl]="quickLinkForm.controls.actionType"
        optionLabel="label"
        optionValue="value"
        placeholder="Select action type"
        styleClass="w-full"
      >
        <ng-template let-option pTemplate="item">
          <div class="flex align-items-center gap-2">
            <i [class]="option.icon"></i>
            <div>
              <div class="font-semibold">{{ option.label }}</div>
              <div class="text-sm text-500">
                {{ option.description }}
              </div>
            </div>
          </div>
        </ng-template>
      </p-select>
      <small class="field-help">What action will this link perform?</small>
    </div>
  </div>
</ng-template>

<!-- ============================================ -->
<!-- TEMPLATE: Configuration Content             -->
<!-- ============================================ -->
<ng-template #configurationContent>
  <div class="field-group">
    <!-- Usage Limit Type - Radio Cards (3 columns on desktop) -->
    <div class="field">
      <label class="field-label">
        Usage Limit
        <span class="required-indicator">*</span>
      </label>

      <div class="radio-card-group-3">
        @for (option of usageLimitTypeOptions; track option.value) {
          <div
            class="radio-card"
            [class.selected]="
              quickLinkForm.controls.usageLimitType.value === option.value
            "
            (click)="
              quickLinkForm.controls.usageLimitType.setValue(option.value)
            "
          >
            <div class="radio-card-content">
              <div class="radio-card-header">
                <p-radioButton
                  [value]="option.value"
                  [formControl]="quickLinkForm.controls.usageLimitType"
                  [inputId]="'usage-limit-' + option.value"
                />
                <label
                  [for]="'usage-limit-' + option.value"
                  class="radio-label"
                  >{{ option.label }}</label
                >
              </div>
              <p class="radio-description">{{ option.description }}</p>
            </div>
          </div>
        }
      </div>
    </div>

    <!-- Max Uses Input (shown when Limited Use is selected) -->
    @if (isLimitedUse()) {
      <div class="field max-uses-field">
        <label for="maxUses" class="field-label">
          Maximum Uses
          <span class="required-indicator">*</span>
        </label>
        <div class="flex align-items-center gap-2">
          <p-inputNumber
            inputId="maxUses"
            [formControl]="quickLinkForm.controls.maxUses"
            [min]="minMaxUses()"
            [max]="10000"
            placeholder="100"
            [showButtons]="true"
            styleClass="max-uses-input"
            [class.ng-invalid]="
              quickLinkForm.controls.maxUses.touched &&
              quickLinkForm.controls.maxUses.invalid
            "
          />
        </div>

        <!-- Current usage info (edit mode only) -->
        @if (mode() === "edit" && isLimitedUse() && currentUsageCount() > 0) {
          <small class="field-help" style="margin-top: 0.25rem">
            <i class="pi pi-info-circle"></i>
            Currently used {{ currentUsageCount() }} times (minimum must be
            {{ minMaxUses() }})
          </small>
        }

        <!-- Validation error -->
        @if (
          quickLinkForm.controls.maxUses.touched &&
          quickLinkForm.controls.maxUses.invalid
        ) {
          <small class="field-error">
            Enter a number between {{ minMaxUses() }} and 10,000
          </small>
        }

        @if (mode() === "edit" && isLimitedUse()) {
          <!-- Usage Stats -->
          <div class="usage-stats-container">
            <!-- Normal State: Available uses shown -->
            @if (!hasReachedLimit() && !isApproachingLimit()) {
              <small class="field-help usage-success">
                <i class="pi pi-check-circle"></i>
                {{ currentUsageCount() }} used • {{ availableUses() }} available
                ({{ usagePercentage() }}%)
              </small>
            }
            <!-- Approaching Limit: Warning -->
            @if (isApproachingLimit()) {
              <small class="field-help usage-warning">
                <i class="pi pi-exclamation-triangle"></i>
                {{ currentUsageCount() }} used • {{ availableUses() }} remaining
                ({{ usagePercentage() }}%)
              </small>
            }
            <!-- Limit Reached: Danger -->
            @if (hasReachedLimit()) {
              <small class="field-help usage-danger">
                <i class="pi pi-times-circle"></i>
                Usage limit reached • 0 available (100%)
              </small>
            }
          </div>
        }
      </div>
    }

    <p-divider />

    <!-- Checkbox and Date Row (2 columns on desktop) -->
    <div class="field-row-2">
      <!-- Checkboxes Container -->
      <div class="field-checkboxes-group">
        <!-- Requires Login Checkbox -->
        <div class="field-checkbox">
          <p-checkbox
            [formControl]="quickLinkForm.controls.requiresLogin"
            [binary]="true"
            inputId="requiresLogin"
          />
          <label for="requiresLogin" class="checkbox-label">
            <span class="checkbox-title">Requires Customer Login</span>
            @if (quickLinkForm.controls.actionType.value === 2) {
              <span class="checkbox-description text-orange-600"
                >Required for Process Now action type</span
              >
            } @else {
              <span class="checkbox-description"
                >Customer must be logged into their account to use this
                link</span
              >
            }
          </label>
        </div>

        <!-- Requires Confirmation Checkbox -->
        <div class="field-checkbox">
          <p-checkbox
            [formControl]="quickLinkForm.controls.requiresConfirmation"
            [binary]="true"
            inputId="requiresConfirmation"
          />
          <label for="requiresConfirmation" class="checkbox-label">
            <span class="checkbox-title">Requires Confirmation</span>
            @if (quickLinkForm.controls.actionType.value === 2) {
              <span class="checkbox-description text-orange-600"
                >Required for Process Now action type</span
              >
            } @else {
              <span class="checkbox-description"
                >Customer must confirm before the action is processed</span
              >
            }
          </label>
        </div>
      </div>

      <!-- Expiration Date -->
      <div class="field">
        <label for="expiredUtc" class="field-label"
          >Expiration Date (optional)</label
        >
        <p-datepicker
          inputId="expiredUtc"
          [formControl]="quickLinkForm.controls.expiredUtc"
          [showIcon]="true"
          [minDate]="today"
          dateFormat="M d, yy"
          placeholder="Select expiration date"
          styleClass="w-full"
          [showButtonBar]="true"
          appendTo="body"
        />
        <small class="field-help"
          >Link will stop working after this date. Leave blank for no
          expiration.</small
        >
      </div>
    </div>
  </div>
</ng-template>

<!-- ============================================ -->
<!-- TEMPLATE: Redirect Settings Content         -->
<!-- ============================================ -->
<ng-template #redirectContent>
  <div class="field-group">
    <p class="section-intro">What should happen after the action completes?</p>

    <!-- Redirect Type - Radio Cards -->
    <div class="radio-card-group">
      @for (option of redirectTypeOptions; track option.value) {
        <div
          class="radio-card"
          [class.selected]="
            quickLinkForm.controls.redirectType.value === option.value
          "
          (click)="quickLinkForm.controls.redirectType.setValue(option.value)"
        >
          <div class="radio-card-content">
            <div class="radio-card-header">
              <p-radioButton
                [value]="option.value"
                [formControl]="quickLinkForm.controls.redirectType"
                [inputId]="'redirect-type-' + option.value"
              />
              <label
                [for]="'redirect-type-' + option.value"
                class="radio-label"
                >{{ option.label }}</label
              >
            </div>
            <p class="radio-description">{{ option.description }}</p>

            <!-- Thank You Page Message (nested in Thank You Page card) -->
            @if (option.value === 0 && isThankYouPage()) {
              <div class="nested-field">
                <label for="redirectMessage" class="nested-field-label"
                  >Custom Message</label
                >
                <textarea
                  pTextarea
                  inputId="redirectMessage"
                  [formControl]="quickLinkForm.controls.redirectMessage"
                  rows="3"
                  class="w-full"
                  placeholder="Your subscription has been resumed successfully!"
                ></textarea>
                <small class="field-help"
                  >This message will be displayed to the customer</small
                >
              </div>
            }

            <!-- Custom URL + UTM Parameters (nested in Custom URL card) -->
            @if (option.value === 2 && isCustomUrl()) {
              <div class="nested-field">
                <label for="redirectUrl" class="nested-field-label">
                  URL
                  <span class="required-indicator">*</span>
                </label>
                <input
                  pInputText
                  inputId="redirectUrl"
                  [formControl]="quickLinkForm.controls.redirectUrl"
                  type="url"
                  class="w-full"
                  placeholder="https://example.com/success"
                  [class.ng-invalid]="
                    quickLinkForm.controls.redirectUrl.touched &&
                    quickLinkForm.controls.redirectUrl.invalid
                  "
                />
                @if (
                  quickLinkForm.controls.redirectUrl.touched &&
                  quickLinkForm.controls.redirectUrl.invalid
                ) {
                  <small class="field-error"
                    >Please enter a valid URL starting with http:// or
                    https://</small
                  >
                }

                <!-- Progressive Disclosure: UTM Parameters -->
                <div class="utm-section">
                  <button
                    type="button"
                    class="collapsible-trigger"
                    (click)="toggleUtmParams()"
                  >
                    <i
                      class="pi"
                      [class.pi-chevron-down]="!showUtmParams()"
                      [class.pi-chevron-up]="showUtmParams()"
                    ></i>
                    <span class="collapsible-title">UTM Parameters</span>
                    <span class="collapsible-subtitle"
                      >(Optional tracking)</span
                    >
                    @if (hasUtmParams() && !showUtmParams()) {
                      <p-tag value="Configured" severity="info" class="ml-2" />
                    }
                  </button>

                  @if (showUtmParams()) {
                    <div class="collapsible-content">
                      <div class="utm-grid-2x2">
                        <div class="utm-field">
                          <label for="utmSource" class="utm-label"
                            >Source</label
                          >
                          <input
                            pInputText
                            inputId="utmSource"
                            [formControl]="quickLinkForm.controls.utmSource"
                            class="w-full"
                            placeholder="e.g., email"
                          />
                        </div>

                        <div class="utm-field">
                          <label for="utmMedium" class="utm-label"
                            >Medium</label
                          >
                          <input
                            pInputText
                            inputId="utmMedium"
                            [formControl]="quickLinkForm.controls.utmMedium"
                            class="w-full"
                            placeholder="e.g., quick_link"
                          />
                        </div>

                        <div class="utm-field">
                          <label for="utmCampaign" class="utm-label"
                            >Campaign</label
                          >
                          <input
                            pInputText
                            inputId="utmCampaign"
                            [formControl]="quickLinkForm.controls.utmCampaign"
                            class="w-full"
                            placeholder="e.g., winback_2025"
                          />
                        </div>

                        <div class="utm-field">
                          <label for="utmContent" class="utm-label"
                            >Content</label
                          >
                          <input
                            pInputText
                            inputId="utmContent"
                            [formControl]="quickLinkForm.controls.utmContent"
                            class="w-full"
                            placeholder="e.g., resume_link"
                          />
                        </div>
                      </div>
                    </div>
                  }
                </div>

                <!-- Custom URL Preview with UTM Parameters -->
                @if (customUrlPreview()) {
                  <div class="custom-url-preview">
                    <div class="preview-header">
                      <i class="pi pi-eye"></i>
                      <span class="preview-title">Preview</span>
                    </div>
                    <div class="preview-url-box">
                      <code class="preview-url-text">{{
                        customUrlPreview()
                      }}</code>
                      <p-button
                        icon="pi pi-copy"
                        [text]="true"
                        [rounded]="true"
                        (onClick)="copyUrlToClipboard(customUrlPreview()!)"
                        pTooltip="Copy preview URL"
                        tooltipPosition="left"
                        size="small"
                        styleClass="preview-copy-btn"
                      />
                    </div>
                    <small class="preview-help">
                      <i class="pi pi-info-circle"></i>
                      This is the final URL customers will be redirected to
                    </small>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </div>
  </div>
</ng-template>
